import { Waves, Wind, SlidersHorizontal, Sparkles, ArrowRight } from "lucide-react";

const features = [
  {
    icon: Wind,
    title: "Bubble Massage",
    tagline: "Air-blower effervescence",
    body: "A whisper-quiet air blower drives a perimeter floor-jet system, releasing thousands of fine bubbles for soft, full-body effervescent immersion. Ideal for relaxation and circulation.",
    specs: ["≈ 800W silent blower", "Perimeter floor jets", "Adjustable intensity"],
    specAnchor: "spec-bubble-massage",
  },
  {
    icon: Waves,
    title: "Surf Massage",
    tagline: "Directional hydro therapy",
    body: "Independently controlled hydro surf jets deliver targeted muscle relief along the back, lumbar and legs. Each nozzle is directionally adjustable for personalised therapy.",
    specs: ["High-pressure pump", "Adjustable nozzles", "Back & leg zones"],
    specAnchor: "spec-surf-massage",
  },
  {
    icon: SlidersHorizontal,
    title: "Smart Panel Control",
    tagline: "Digital touch console",
    body: "A rim-mounted digital touch panel orchestrates jets, blower, lighting and a safety timer through one illuminated interface. No physical knobs, no exposed wiring — clean and intuitive.",
    specs: ["Capacitive touch", "Multi-function timer", "Splash-proof IPX4"],
    specAnchor: "spec-smart-panel",
  },
  {
    icon: Sparkles,
    title: "Magical Lights",
    tagline: "Underwater chromotherapy",
    body: "Submerged LED chromotherapy cycles through a calibrated palette — warm amber, deep blue, emerald, violet and soft white — for spa-grade ambience and mood-lit immersion.",
    specs: ["Multi-colour cycle", "Low-voltage 12V", "Auto / manual modes"],
    specAnchor: "spec-magical-lights",
  },
];

const faqAnchors = [
  { label: "What's included with the bathtub?", href: "#faq-is-the-drainer-and-faucet-included" },
  { label: "Construction & materials", href: "#faq-what-is-the-vela-sp1836-bathtub-made-of" },
  { label: "Support frame details", href: "#faq-what-support-frame-does-it-use" },
  { label: "Minimum order quantity", href: "#faq-what-is-the-minimum-order-quantity" },
];

export const VelaWellnessFeatures = () => {
  return (
    <section
      id="wellness-features"
      className="border-y border-amber-500/15 bg-gradient-to-b from-black via-neutral-950 to-black py-20"
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">
            Wellness Suite · Integrated Spa Functions
          </p>
          <h2 className="font-serif text-3xl text-neutral-50 sm:text-4xl">
            Four Systems.{" "}
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
              One Bathtub.
            </span>
          </h2>
          <div className="mx-auto mt-4 h-px w-16 bg-amber-500/60" />
          <p className="mt-5 text-neutral-400 leading-relaxed">
            VELA SP1836 layers bubble massage, surf hydro therapy, a smart digital panel and
            chromotherapy lighting into a single freestanding shell. Tap any feature below to
            jump straight to its technical entry.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <article
              key={f.title}
              className="group relative flex flex-col overflow-hidden rounded-sm border border-amber-500/15 bg-black/40 p-6 transition-all hover:border-amber-400/50 hover:bg-black/60"
            >
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-amber-500/5 blur-2xl transition-opacity group-hover:bg-amber-500/15" />
              <div className="relative flex flex-1 flex-col">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/15 to-transparent">
                  <f.icon className="h-5 w-5 text-amber-300" />
                </div>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/70">
                  {f.tagline}
                </p>
                <h3 className="mt-1 font-serif text-xl text-neutral-50">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400">{f.body}</p>

                <ul className="mt-5 space-y-1.5 border-t border-neutral-800 pt-4">
                  {f.specs.map((s) => (
                    <li
                      key={s}
                      className="flex items-center gap-2 text-xs text-neutral-300"
                    >
                      <span className="h-1 w-1 rounded-full bg-amber-400" />
                      {s}
                    </li>
                  ))}
                </ul>

                <a
                  href={`#${f.specAnchor}`}
                  className="mt-6 inline-flex items-center gap-1.5 self-start border-b border-amber-400/30 pb-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300 transition-colors hover:border-amber-300 hover:text-amber-200"
                  aria-label={`Jump to ${f.title} in technical specs`}
                >
                  View in specs
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 rounded-sm border border-amber-500/15 bg-black/30 p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/70">
            Jump to FAQ answers
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {faqAnchors.map((a) => (
              <a
                key={a.href}
                href={a.href}
                className="inline-flex items-center gap-1.5 rounded-sm border border-amber-500/20 bg-black/40 px-3 py-1.5 text-xs text-neutral-300 transition-colors hover:border-amber-400/50 hover:text-amber-200"
              >
                {a.label}
                <ArrowRight className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-neutral-500">
          All four systems independently controlled · Layer any combination simultaneously
        </p>
      </div>
    </section>
  );
};
